import { useId, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatQuantity } from '@fewerbags/core';
import type { ProductDto } from '@fewerbags/schema';
import { api } from '../../lib/api';
import { useDebouncedValue } from '../../lib/useDebouncedValue';
import { storeQueryKeys } from '../../pages/stores/queryKeys';

/**
 * Product search-as-you-type (by ingredient name, brand or label — see
 * `listProducts` in the API). Used to attach a per-product shelf-life override
 * or a household default product to an ingredient.
 */
export function ProductPicker({
  id,
  label,
  onSelect,
  placeholder = 'Search products…',
}: {
  id: string;
  label: string;
  onSelect: (product: ProductDto) => void;
  placeholder?: string;
}) {
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const listId = useId();
  const debounced = useDebouncedValue(text.trim(), 250);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const search = useQuery({
    queryKey: storeQueryKeys.productSearch(debounced),
    queryFn: () => api.stores.products.list({ q: debounced, limit: 10 }),
    enabled: debounced.length > 0,
    staleTime: 30_000,
  });

  const results = search.data?.products ?? [];

  function pick(product: ProductDto) {
    onSelect(product);
    setText('');
    setOpen(false);
  }

  return (
    <div className="relative flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
      </label>
      <input
        id={id}
        role="combobox"
        aria-expanded={open && results.length > 0}
        aria-controls={listId}
        aria-autocomplete="list"
        autoComplete="off"
        placeholder={placeholder}
        className="min-h-11 rounded-lg border border-line bg-surface px-3 text-base text-ink placeholder:text-muted"
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
          setHighlight(0);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          blurTimer.current = setTimeout(() => setOpen(false), 150);
        }}
        onKeyDown={(e) => {
          if (!open || results.length === 0) return;
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlight((h) => Math.min(h + 1, results.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((h) => Math.max(h - 1, 0));
          } else if (e.key === 'Enter') {
            const hit = results[highlight];
            if (hit) {
              e.preventDefault();
              pick(hit);
            }
          } else if (e.key === 'Escape') {
            setOpen(false);
          }
        }}
      />
      {open && debounced.length > 0 && results.length === 0 && !search.isFetching ? (
        <p className="text-xs text-muted">No products match “{debounced}”.</p>
      ) : null}
      {open && results.length > 0 ? (
        <ul
          id={listId}
          role="listbox"
          className="absolute top-full z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-line bg-surface shadow-lg"
        >
          {results.map((p, i) => (
            <li key={p.id} role="option" aria-selected={i === highlight}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(p)}
                className={`flex min-h-11 w-full flex-col items-start px-3 py-1.5 text-left text-sm ${
                  i === highlight ? 'bg-accent-soft' : ''
                }`}
              >
                <span className="font-medium text-ink">{p.label}</span>
                <span className="text-xs text-muted">
                  {p.ingredientName} · {formatQuantity(p.packageQty, p.packageUnit)}
                  {p.brand ? ` · ${p.brand}` : ''}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
